import React, { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { Field, Form, FormSpy } from 'react-final-form'
import { OnChange } from 'react-final-form-listeners'
import { useIntl } from 'react-intl'

import { addMinutes, isBefore, isValid, parse, startOfDay, subDays } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import { FormApi } from 'final-form'
import { range } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { thisFieldIsRequiredErrorMessage } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/messages'
import { matchFinderForObjects } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/commonUtils'
import { toFakeUTCISOString } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/timeZoneUtils'
import {
  generateTypeAheadSectionList,
  SECTION_LIST_HEADER,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/typeAheadUtils'
import { Episode } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import { DEFAULT_VIDEO_PLATFORM, VIDEO_PLATFORM } from '@lyrahealth-inc/shared-app-logic/src/models/modality/Modality'
import { Address } from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'
import { MEETING_FORMATS } from '@lyrahealth-inc/shared-app-logic/src/models/session/Session'

import { BodyText } from '../../atoms/bodyText/BodyText'
import { BaseInput } from '../../atoms/formElements/BaseInput'
import { CheckboxRFF } from '../../atoms/formElements/Checkbox'
import { FormContainer } from '../../atoms/formElements/formUtils'
import { SelectFieldRFF } from '../../atoms/formElements/SelectField'
import { TypeAheadRFF } from '../../atoms/formElements/typeAhead/TypeAhead'
import { TypeAheadCustomOptionsConfig } from '../../atoms/formElements/typeAhead/TypeAheadCustomOptionsConfig'
import { HPISelfPayProviderBanner } from '../../atoms/hpiSelfPayProviderBanner/HPISelfPayProviderBanner'
import { InfoIcon } from '../../atoms/icons/InfoIcon'
import { SelfPayContinuationOfCareBanner } from '../../atoms/selfPayContinuationOfCareBanner/SelfPayContinuationOfCareBanner'
import { Tooltip, TOOLTIP_TRIGGER_ICON } from '../../atoms/tooltip/Tooltip'
import {
  ModalityOption,
  useBookingDetailsWithModalityOptions,
  VIDEO_PLATFORM_OPTION_VALUE,
} from '../../hooks/useBookingDetailsWithModalityOptions'
import { GetEventsFn, useCalendarSlots } from '../../hooks/useCalendarSlots'
import { useLocaleDateFormat } from '../../hooks/useLocaleDateFormat'
import { BodyTextSize } from '../../styles/typeStyles'
import { CalendarClient, GetBookingDetailsForClient } from '../../ui-models/clients/CalendarClient'
import { tID } from '../../utils/utils'
import { ProviderCalendarDateInput } from '../providerCalendarSlotSelectionContent/ProviderCalendarDateInput'
import { ProviderCalendarTimeSlotSelector } from '../providerCalendarSlotSelectionContent/ProviderCalendarTimeSlotSelector'

const Container = styled.View(({ theme }) => ({
  flexDirection: 'column',
  gap: theme.spacing['16px'],
  width: '100%',
}))

const Row = styled.View(({ theme }) => ({
  flexDirection: 'row',
  gap: theme.spacing['16px'],
}))

const FieldContent = styled.View(({ theme }) => ({
  flex: 1,
  gap: theme.spacing['8px'],
}))

const RecurringRow = styled.View(({ theme }) => ({
  flexDirection: 'row',
  gap: theme.spacing['8px'],
}))

const TimeslotContainer = styled(BaseInput)({
  flex: 1,
  marginBottom: 0,
})

const DateContainer = styled(ProviderCalendarDateInput)({
  flex: 1,
})

type ProviderCalendarScheduleSessionFormValues = {
  client: string
  date: string
  startTime: string
  meetingFormat: string
  sessionType: string
  recurring: boolean
  frequency: number
  repeatFor: number
}

export type ProviderCalendarScheduleSessionContentProps = {
  suggestedClients: CalendarClient[]
  clients: CalendarClient[]
  getEvents?: GetEventsFn
  slotMaxTime: string
  slotMinTime: string
  rangeToAdd?: { start: string; end: string; startLocal: string; endLocal: string } | null
  timeZone?: string
  getBookingDetailsForClient?: GetBookingDetailsForClient
  bookableInPersonLocations?: Address[] | null
  enableBackdatedAppointments?: boolean
  isSelfPayEnabled?: boolean
  isLegacyProvider?: boolean
  isLegacyClientListLoading?: boolean
  splitVideoOptions?: boolean
  providerVideoPlatform?: VIDEO_PLATFORM | null
  // Legacy providers don't have client booking details, so their modality options are built from
  // the provider's own video/in-person preferences instead (mirroring the availability modal).
  videoPreference?: boolean
  inPersonPreference?: boolean
}

export type ProviderCalendarScheduleSessionContentRef = {
  submit: () => {
    startTime: string
    endTime: string
    slotEndTime: string
    client: string
    duration: number
    meetingFormat: string
    episode?: Episode
    recurring: boolean
    frequency: number
    repeatFor: number
    address?: string
    videoPlatform?: VIDEO_PLATFORM
    sessionType?: string
  } | null
  reset: () => void
}

const THIRTY_MINUTES_IN_MS = 30 * 60 * 1000

export const ProviderCalendarScheduleSessionContent = forwardRef<
  ProviderCalendarScheduleSessionContentRef,
  ProviderCalendarScheduleSessionContentProps
>(
  (
    {
      clients,
      getEvents,
      suggestedClients,
      slotMaxTime,
      slotMinTime,
      rangeToAdd,
      timeZone,
      getBookingDetailsForClient,
      bookableInPersonLocations,
      enableBackdatedAppointments,
      isSelfPayEnabled,
      isLegacyProvider,
      isLegacyClientListLoading,
      splitVideoOptions,
      providerVideoPlatform,
      videoPreference,
      inPersonPreference,
    },
    ref,
  ) => {
    const { formatMessage } = useIntl()
    const submitForm = useRef<() => Promise<ProviderCalendarScheduleSessionFormValues | undefined> | undefined>()
    const scheduleSessionForm = useRef<FormApi<ProviderCalendarScheduleSessionFormValues> | null>(null)
    const [currentDate, setCurrentDate] = useState<string>('')
    const [selectedClient, setSelectedClient] = useState<string | null>(null)
    const { locale } = useIntl()

    const { bookingDetails, isLoadingClientBookingDetails, modalityOptions } = useBookingDetailsWithModalityOptions({
      clientId: selectedClient,
      enabled: !!selectedClient,
      getBookingDetailsForClient,
      bookableInPersonLocations,
      splitVideoOptions,
    })

    const RETRO_APPOINTMENT_MAX_DAYS = 90
    const canBookRetroactively = !!enableBackdatedAppointments
    const sessionDuration = bookingDetails?.sessionDuration ?? 50
    const sessionTypeOptions = bookingDetails?.sessionTypeOptions ?? []
    const frequencyOptions = useMemo(
      () => bookingDetails?.frequencyOptions ?? [{ label: 'Repeat every two weeks', value: 14 }],
      [bookingDetails],
    )

    // Legacy providers have no client booking details to source modality options from, so we build
    // them from the provider's own video/in-person preferences + addresses — matching the options
    // shown in the availability modal (ProviderCalendarModalitySelector).
    const legacyMeetingFormatOptions = useMemo<ModalityOption[]>(() => {
      const options: ModalityOption[] = []
      if (videoPreference) {
        options.push({
          label: formatMessage({ defaultMessage: 'Video', description: 'Meeting format option for a video session' }),
          value: MEETING_FORMATS.VIDEO,
        })
      }
      if (inPersonPreference && bookableInPersonLocations && bookableInPersonLocations.length > 0) {
        bookableInPersonLocations.forEach((address, index) => {
          options.push({
            label: address.street1 || 'In-person Location '.concat(index.toString()),
            value: MEETING_FORMATS.IN_PERSON.concat(address.id?.toString() || index.toString()),
            address: [address.street1, address.city, address.state].filter(Boolean).join(', '),
          })
        })
      }
      return options
    }, [videoPreference, inPersonPreference, bookableInPersonLocations, formatMessage])

    const meetingFormatOptions = isLegacyProvider ? legacyMeetingFormatOptions : modalityOptions

    const theme = useTheme()

    const localeDateFormat = useLocaleDateFormat()
    useImperativeHandle(
      ref,
      () => ({
        submit: () => {
          if (!bookingDetails && !isLegacyProvider) {
            return null
          }

          scheduleSessionForm.current?.submit()
          const formState = scheduleSessionForm.current?.getState()
          const isValid = scheduleSessionForm.current?.getState().valid
          if (isValid) {
            const startTime = parse(
              formState!.values.startTime,
              'hh:mm a',
              parse(formState!.values.date, localeDateFormat, new Date()),
            )
            const endTime = addMinutes(startTime, sessionDuration)
            const selectedOption = meetingFormatOptions.find(
              (option) => option.value === formState!.values.meetingFormat,
            )
            const sessionType = bookingDetails?.sessionTypeOptions?.find(
              (option) => option.value === formState!.values.sessionType,
            )?.value
            // Use toFakeUTCISOString to preserve the local time values as "fake UTC" for transport.
            // The backend will interpret these as the selected calendar timezone's local time.
            return {
              startTime: toFakeUTCISOString(startTime),
              endTime: toFakeUTCISOString(endTime),
              slotEndTime: toFakeUTCISOString(
                new Date(Math.ceil(endTime.getTime() / THIRTY_MINUTES_IN_MS) * THIRTY_MINUTES_IN_MS),
              ),
              client: formState!.values.client,
              meetingFormat:
                bookableInPersonLocations && formState!.values.meetingFormat.includes(MEETING_FORMATS.IN_PERSON)
                  ? MEETING_FORMATS.IN_PERSON
                  : selectedOption?.videoPlatform
                    ? MEETING_FORMATS.VIDEO
                    : formState!.values.meetingFormat,
              duration: sessionDuration,
              episode: bookingDetails?.episode,
              recurring: formState!.values.recurring,
              frequency: formState!.values.frequency || frequencyOptions[0]?.value || 0,
              repeatFor: formState!.values.repeatFor || 2,
              ...(sessionType && { sessionType }),
              ...(bookableInPersonLocations &&
                formState!.values.meetingFormat.includes(MEETING_FORMATS.IN_PERSON) && {
                  address: selectedOption?.address,
                }),
              ...(selectedOption?.videoPlatform && {
                videoPlatform: selectedOption.videoPlatform,
              }),
            }
          }
          return null
        },
        reset: () => {
          scheduleSessionForm.current?.reset()
          setSelectedClient(null)
        },
        buildPayload: () => {
          return scheduleSessionForm.current?.getState().values ?? null
        },
      }),
      [
        bookingDetails,
        frequencyOptions,
        bookableInPersonLocations,
        isLegacyProvider,
        localeDateFormat,
        sessionDuration,
        meetingFormatOptions,
      ],
    )

    const defaultVideoValue = splitVideoOptions
      ? VIDEO_PLATFORM_OPTION_VALUE[providerVideoPlatform ?? DEFAULT_VIDEO_PLATFORM]
      : MEETING_FORMATS.VIDEO

    // startLocal is "fake UTC"; see toFakeUTCISOString docs
    const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
    const initialValues: ProviderCalendarScheduleSessionFormValues = {
      client: '',
      date: rangeToAdd?.startLocal
        ? formatInTimeZone(new Date(rangeToAdd.startLocal), 'UTC', localeDateFormat)
        : formatInTimeZone(new Date(), timeZone ?? browserTimeZone, localeDateFormat),
      startTime: rangeToAdd ? formatInTimeZone(new Date(rangeToAdd.startLocal), 'UTC', 'hh:mm a') : '',
      meetingFormat: !!bookableInPersonLocations?.length && !splitVideoOptions ? '' : defaultVideoValue,
      sessionType: '',
      recurring: false,
      frequency: 0,
      repeatFor: 0,
    }

    const { allAvailableTimeSlots, blockedTimeSlotValues, slotMap } = useCalendarSlots({
      sessionDuration,
      slotMinTime,
      slotMaxTime,
      date: currentDate,
      getEvents,
      showSlots: true,
      roundUp: false,
      includePastTimeSlotsForToday: canBookRetroactively,
    })

    const validate = (values: ProviderCalendarScheduleSessionFormValues) => {
      const errors: Partial<Record<keyof ProviderCalendarScheduleSessionFormValues, string>> = {}
      if (!values.client) {
        errors.client = formatMessage({
          defaultMessage: 'Client is required',
          description: 'Error message for when provider does not select a client',
        })
      }
      if (!values.date) {
        errors.date = formatMessage({
          defaultMessage: 'Date is required',
          description: 'Error message for when provider does not select a date',
        })
      }

      if (!errors.date) {
        const parsed = parse(values.date, localeDateFormat, new Date())
        const today = startOfDay(new Date())

        if (!isValid(parsed)) {
          errors.date = formatMessage({
            defaultMessage: 'Invalid date',
            description: 'Error message for when provider enters an invalid date',
          })
        }

        if (canBookRetroactively) {
          if (isBefore(startOfDay(parsed), subDays(today, RETRO_APPOINTMENT_MAX_DAYS))) {
            errors.date = formatMessage({
              defaultMessage: 'Date is more than 90 days in the past',
              description: 'Error message for when a provider tries to add a session more than 90 days in the past',
            })
          }
        } else {
          if (isBefore(startOfDay(parsed), today)) {
            errors.date = formatMessage({
              defaultMessage: 'Date is in the past',
              description: 'Error message for when provider tries to add availability for a date that is in the past',
            })
          }
        }
      }

      if (!values.startTime) {
        errors.startTime = formatMessage({
          defaultMessage: 'Start time is required',
          description: 'Error message for when provider does not select a start time',
        })
      }

      if (!values.meetingFormat && values.client) {
        errors.meetingFormat = formatMessage({
          defaultMessage: 'Modality is required',
          description: 'Error message for when provider does not select a modality',
        })
      }
      if (sessionTypeOptions.length > 0 && !values.sessionType) {
        errors.sessionType = formatMessage(thisFieldIsRequiredErrorMessage)
      }
      return errors
    }

    const allClients = useMemo(() => {
      return [...suggestedClients, ...clients]
    }, [clients, suggestedClients])

    return (
      <Form
        initialValues={initialValues}
        onSubmit={() => {}}
        validate={validate}
        render={({ handleSubmit, form, values }) => {
          submitForm.current = handleSubmit as () => Promise<ProviderCalendarScheduleSessionFormValues | undefined>
          scheduleSessionForm.current = form
          const showRecurring =
            !!values.startTime && slotMap[values.startTime]?.extendedProps?.lyraEventType === 'bookable_recurring'

          const resetRecurring = () => {
            form.change('recurring', false)
            form.change('frequency', 0)
            form.change('repeatFor', 0)
          }

          const selectedOptionHelperText = meetingFormatOptions.find(
            (option) => option.value === values.meetingFormat,
          )?.helperText
          const selectedSessionType = sessionTypeOptions.find((option) => option.value === values.sessionType)

          return (
            <FormContainer handleSubmit={handleSubmit}>
              <Container>
                <Field
                  name='client'
                  component={TypeAheadRFF}
                  label={formatMessage({
                    defaultMessage: 'Search',
                    description: 'Label for search field to select a client',
                  })}
                  placeholder={formatMessage({
                    defaultMessage: 'Search by name or Lyra ID',
                    description: 'Placeholder text for search field to select a client',
                  })}
                  baseInputStyle={{ marginBottom: 0 }}
                  inputContainerStyle={{ borderRadius: 8 }}
                  multiSelect={false}
                  onValueChange={(value: string) => {
                    form.change('sessionType', '')
                    setSelectedClient(value)
                  }}
                  dataSource='clients'
                  postExternalMatches={async ({ input }: { input: string }) => {
                    const clientById = allClients.find((client) => client.id.toLowerCase() === input.toLowerCase())
                    if (clientById) {
                      return [clientById.name]
                    }
                    return matchFinderForObjects(
                      allClients,
                      (client) => [
                        client.name,
                        client.legalFirstName,
                        client.legalLastName,
                        client.legalFirstName && client.legalLastName
                          ? `${client.legalFirstName} ${client.legalLastName}`
                          : undefined,
                      ],
                      (client) => client.name,
                      input,
                      locale,
                    )
                  }}
                  customOptionsConfig={
                    new TypeAheadCustomOptionsConfig(
                      generateTypeAheadSectionList(
                        [
                          {
                            title: SECTION_LIST_HEADER.SUGGESTED_CLIENTS,
                            items: suggestedClients,
                            useEyebrow: true,
                          },
                          {
                            items: clients,
                          },
                        ],
                        'name',
                        true,
                        'id',
                      ),
                      undefined,
                      undefined,
                      'name',
                      'id',
                    )
                  }
                  showLoadingIndicator={isLoadingClientBookingDetails || isLegacyClientListLoading}
                />
                {isSelfPayEnabled &&
                  !isLoadingClientBookingDetails &&
                  bookingDetails?.isSelfPayAvailable &&
                  bookingDetails?.clientDetails.customer_support_self_pay &&
                  !bookingDetails?.isHpiOnlyCoverage &&
                  !bookingDetails?.hpiSelfPayBannerVariant && (
                    <SelfPayContinuationOfCareBanner
                      clientDetails={bookingDetails?.clientDetails}
                      sessionsRemaining={bookingDetails?.sessionsRemaining}
                    />
                  )}
                {!isLoadingClientBookingDetails && bookingDetails?.hpiSelfPayBannerVariant && (
                  <HPISelfPayProviderBanner
                    variant={bookingDetails.hpiSelfPayBannerVariant}
                    clientFirstName={
                      bookingDetails.clientDetails?.preferred_first_name ||
                      bookingDetails.clientDetails?.first_name ||
                      ''
                    }
                    customerName={bookingDetails.clientDetails?.displayed_employer ?? '-'}
                  />
                )}
                <Row>
                  <Field
                    name='date'
                    render={({ input, meta }) => (
                      <DateContainer
                        value={input.value}
                        onChange={(value) => {
                          input.onChange(value)
                          setCurrentDate(value)
                        }}
                        error={meta.touched ? meta.error || meta.submitError : undefined}
                        useBuiltInValidation={false}
                        onFocus={input.onFocus}
                        onBlur={input.onBlur}
                        preventOpenOnFocus
                      />
                    )}
                  />
                  <TimeslotContainer
                    label={formatMessage({
                      defaultMessage: 'Start time',
                      description: 'Title for selectors where providers select a start time for a session',
                    })}
                  >
                    <Field
                      name='startTime'
                      render={({ input, meta }) => (
                        <ProviderCalendarTimeSlotSelector
                          allOptions={allAvailableTimeSlots}
                          blockedTimeSlotValues={blockedTimeSlotValues}
                          index={0}
                          selectedOption={input.value}
                          selectedOptionList={[input.value]}
                          setSelectedOptionList={(startTimes) => {
                            input.onChange(startTimes[0])
                          }}
                          showDeleteIcon={false}
                          singleTimeSelector={true}
                          error={meta.touched ? meta.error || meta.submitError : undefined}
                          onFocus={input.onFocus}
                          onBlur={input.onBlur}
                        />
                      )}
                    />
                  </TimeslotContainer>
                </Row>
                {rangeToAdd && (
                  <RecurringRow>
                    <Field
                      name='recurring'
                      component={CheckboxRFF}
                      title={formatMessage({
                        defaultMessage: 'Recurring',
                        description: 'Label for checkbox to select if a session is recurring',
                      })}
                      titleColor={showRecurring ? undefined : theme.colors.textInactive}
                      baseInputStyle={{ marginBottom: 0 }}
                      readOnly={!showRecurring}
                    />
                    {!showRecurring && (
                      <Tooltip
                        content={formatMessage({
                          defaultMessage:
                            'This is one-time availability. Make this a recurring availability slot to enable this.',
                          description: 'Tooltip content for recurring checkbox',
                        })}
                        hoverEnabled
                      >
                        <InfoIcon />
                      </Tooltip>
                    )}
                  </RecurringRow>
                )}
                {rangeToAdd && showRecurring && values.recurring && (
                  <Row>
                    <Field
                      name='frequency'
                      component={SelectFieldRFF}
                      label={formatMessage({
                        defaultMessage: 'Frequency',
                        description: 'Label for select field to select a frequency for a recurring session',
                      })}
                      options={frequencyOptions}
                      baseInputStyle={{ marginBottom: 0, flex: 1 }}
                      disablePlaceholder
                    />
                    <Field
                      name='repeatFor'
                      component={SelectFieldRFF}
                      label={formatMessage({
                        defaultMessage: 'Repeats For',
                        description:
                          'Label for select field to select how many times a recurring session should repeat',
                      })}
                      options={range(2, 9).map((number: number) => ({
                        label: formatMessage(
                          {
                            defaultMessage: '{number, plural, one {# session} other {# sessions}}',
                            description:
                              'Label for select field to select how many times a recurring session should repeat',
                          },
                          { number },
                        ),
                        value: number,
                      }))}
                      baseInputStyle={{ marginBottom: 0, flex: 1 }}
                      disablePlaceholder
                    />
                  </Row>
                )}
                <Row>
                  {sessionTypeOptions.length > 0 && (
                    <FieldContent>
                      <Field
                        name='sessionType'
                        component={SelectFieldRFF}
                        label={formatMessage({
                          defaultMessage: 'Session type',
                          description: 'Label for the clinical session type selection field',
                        })}
                        options={sessionTypeOptions}
                        required
                        baseInputStyle={{ marginBottom: 0 }}
                      />
                      {selectedSessionType?.helperText && (
                        <BodyText
                          testID={tID('ScheduleSession-session-type-attendees')}
                          aria-live='polite'
                          accessibilityLiveRegion='polite'
                          size={BodyTextSize.SMALL}
                          color={theme.colors.textSecondary}
                          text={selectedSessionType.helperText}
                        />
                      )}
                    </FieldContent>
                  )}
                  <FieldContent>
                    <Field
                      name='meetingFormat'
                      component={SelectFieldRFF}
                      label={formatMessage({
                        defaultMessage: 'Modality',
                        description: 'Label for select field to select a modality',
                      })}
                      options={meetingFormatOptions}
                      baseInputStyle={{ marginBottom: 0 }}
                      {...(splitVideoOptions && {
                        toolTipContent: formatMessage({
                          defaultMessage: 'To change your default setting go to Settings > Video session preference',
                          description: 'Tooltip explaining how to change default modality setting',
                        }),
                        toolTipTriggerConfig: { icon: TOOLTIP_TRIGGER_ICON.INFO },
                        toolTipHoverEnabled: true,
                      })}
                    />
                    {selectedOptionHelperText && (
                      <BodyText
                        testID={tID('ScheduleSession-ExternalVideoHelperText')}
                        size={BodyTextSize.SMALL}
                        color={theme.colors.textSecondary}
                        text={selectedOptionHelperText}
                      />
                    )}
                  </FieldContent>
                </Row>
              </Container>

              <FormSpy subscription={{}}>
                {() => (
                  <>
                    <OnChange name='startTime'>{resetRecurring}</OnChange>
                    <OnChange name='date'>{resetRecurring}</OnChange>
                  </>
                )}
              </FormSpy>
            </FormContainer>
          )
        }}
      />
    )
  },
)
